import { useApiQuery } from "@gladis/api-client";
import { useT } from "@gladis/ui/i18n";
import type { CurrentUserDto, MemberListDto } from "@gladis/shared";
import type { ReactNode } from "react";

import { DeleteHouseholdDialog, RenameHouseholdForm } from "./DeleteHouseholdDialog.js";
import { InviteMemberForm } from "./InviteMemberForm.js";
import { LeaveHouseholdDialog } from "./LeaveHouseholdDialog.js";
import { MembersList } from "./MembersList.js";
import { PendingInvitesList } from "./PendingInvitesList.js";
import { TransferOwnershipDialog } from "./TransferOwnershipDialog.js";

/**
 * Everything about the household the user is currently in.
 *
 * Assembled here rather than in `apps/app` because the ORDER and the CONDITIONS are identity's
 * business, not the shell's: which controls an Owner sees, which an Admin sees, and who is eligible
 * to receive ownership are all statements about the role model. `apps/app` decides where this
 * appears on a page; it does not decide what an Owner may do.
 *
 * ONE QUERY FEEDS BOTH the member list and the transfer candidate list, deliberately. Deriving
 * candidates from a second source would let the two disagree — a name in the transfer dropdown that
 * is not in the members list above it is the kind of inconsistency nobody reports and everybody
 * notices.
 *
 * Every role-conditional render below has a server-side twin in `MembershipService`. Hiding a
 * control is presentation; the refusal is the rule. If you add a control here, add its refusal
 * there, and test the request rather than the button.
 */
export function HouseholdSettings({
  me,
  onLeft,
  onCreateHousehold,
}: {
  me: CurrentUserDto;
  /** Called after the user stops being a member, so the app can route them onward. */
  onLeft: () => void;
  /** Takes the user to the household-creation flow (FR-046). */
  onCreateHousehold: () => void;
}): ReactNode {
  const t = useT();
  const household = me.activeHousehold;

  const members = useApiQuery<MemberListDto>(
    ["members", household?.id ?? "none"],
    `/households/${household?.id ?? ""}/members`,
    { enabled: household !== null },
  );

  // Someone with no active membership has no household to configure. The shell routes them to
  // onboarding; rendering nothing here avoids a flash of empty sections on the way.
  if (!household) return null;

  const isOwner = household.role === "owner";

  // Only current Admins may receive ownership (FR-015). Derived from the same list the table above
  // renders, so the two cannot drift apart.
  const candidates = (members.data?.members ?? [])
    .filter((member) => member.role === "admin" && !member.isSelf)
    .map((member) => ({ userId: member.userId, displayName: member.displayName }));

  return (
    <>
      <h2 className="mb-4 text-2xl font-semibold">{household.name}</h2>

      {/*
       * Creating an ADDITIONAL household (FR-046).
       *
       * Here rather than only in the header switcher, because a person with one household has no
       * switcher — US4 scenario 1 keeps the header as plain text for them, exactly as 004 shipped
       * it. This is where the capability lives for everyone, and the header's menu is a shortcut
       * for people who already have more than one.
       */}
      <p className="mb-6 text-sm text-muted">
        <button
          type="button"
          className="underline underline-offset-4 hover:text-ink"
          onClick={onCreateHousehold}
        >
          {t("identity.household.createAnother")}
        </button>{" "}
        if you keep a second set of books.
      </p>

      <MembersList householdId={household.id} />

      <div className="mt-10">
        <InviteMemberForm householdId={household.id} />
      </div>

      {/* Directly under the invite form, because the question "did I already invite them?" arises
          at the moment you are about to. */}
      <PendingInvitesList householdId={household.id} />

      {/* Owner-only, and absent rather than disabled for an Admin (AC-H7). A disabled control
          advertises a capability they will never have in this household. */}
      {isOwner ? (
        <TransferOwnershipDialog
          householdId={household.id}
          householdName={household.name}
          candidates={candidates}
          // The mutation invalidates `me` and the member list itself; this is only for the
          // caller's own bookkeeping if it wants any.
          onTransferred={() => undefined}
        />
      ) : null}

      <RenameHouseholdForm household={household} />

      <LeaveHouseholdDialog
        householdId={household.id}
        householdName={household.name}
        isOwner={isOwner}
        onLeft={onLeft}
      />

      {/*
       * LAST ON THE PAGE, and Owner-only (AC-H8). Position is a design decision with a reason: the
       * only irreversible action in the product should not sit above the reversible ones, where a
       * mis-scroll can reach it.
       *
       * Absent rather than disabled for an Admin — see the note in DeleteHouseholdDialog.
       */}
      {isOwner ? <DeleteHouseholdDialog household={household} onDeleted={onLeft} /> : null}
    </>
  );
}
